import { createSelector } from '@reduxjs/toolkit';

const selectThreadsByProject = (state) => state.chatSlice.threadsByProject;
const selectActiveCallsByThreadId = (state) =>
  state.chatSlice.activeCallsByThreadId;

/**
 * The ongoing video call in a project's main chat thread, or null.
 *
 * A call belongs to a thread, not a project, so this resolves the project's
 * main thread first. Factory selector — wrap the call in useMemo keyed on the
 * project id, the way ProjectListItem does for selectProjectById.
 */
export const selectActiveCallForProject = (projectId) =>
  createSelector(
    [selectThreadsByProject, selectActiveCallsByThreadId],
    (threadsByProject, activeCallsByThreadId) => {
      const thread = threadsByProject?.[projectId];
      const threadId = thread?.threadId || thread?.id;
      if (!threadId) return null;
      return activeCallsByThreadId?.[threadId] || null;
    }
  );

const selectMeetingSession = (state) => state.videoChatSlice.meetingSession;
const selectSessionThread = (state) => state.videoChatSlice.sessionThread;

/**
 * Whether the user is already in this project's call. The chat dialog can be
 * minimised while a call runs, so the indicator stays visible to someone who
 * has already joined — they should be offered "open", not "join".
 */
export const selectIsUserInProjectCall = (projectId) =>
  createSelector(
    [selectThreadsByProject, selectMeetingSession, selectSessionThread],
    (threadsByProject, meetingSession, sessionThread) => {
      if (!meetingSession) return false;
      const thread = threadsByProject?.[projectId];
      const threadId = thread?.threadId || thread?.id;
      if (!threadId) return false;
      return (sessionThread?.threadId || sessionThread?.id) === threadId;
    }
  );
